import { Form, FormikProvider, useFormik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	Container,
	GraphQLErrorMessage,
	Heading3,
	Jumbo,
	Paragraph,
	SettingsSection,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import {
	ConversationRevealUploadButton,
	PendingRevealUpload,
	toFieldTypeInput,
} from '../../conversations/reveal';
import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import { useJobTitleOptions } from './EvaluationFormEditor';

export default function EvaluationFormCreator() {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const history = useHistory();

	const options = useJobTitleOptions();

	const revealEnabled = useFeature(graphql.FeatureName.EvaluationReveal);
	const [pendingUpload, setPendingUpload] = useState<PendingRevealUpload | undefined>(undefined);

	const formik = useFormik({
		initialValues: {
			title: [],
			description: [],
			jobTitleIDs: undefined,
			useOnlyInCycle: false,
			showAnswersWhenAllUsersShared: false,
		},
		onSubmit: () => {},
	});

	const [error, attempt] = useErrorBoundary();
	const [create, { loading }] = graphql.useCreateEvaluationFormMutation();
	const [update, { loading: updating }] = graphql.useUpdateEvaluationFormMutation();
	const [saveRevealUpload, { loading: committingUpload }] = graphql.useSaveRevealUploadMutation();
	// Set once the template is created, so a failed upload-commit can be
	// retried without creating a second template. Title item id is required
	// so retry sync updates in place instead of archiving the original item.
	const [createdFormID, setCreatedFormID] = useState<string | undefined>(undefined);
	const [createdTitleItemID, setCreatedTitleItemID] = useState<string | undefined>(undefined);

	const commitPendingUpload = async (evaluationFormID: string): Promise<boolean> => {
		if (!pendingUpload) {
			return true;
		}

		try {
			await saveRevealUpload({
				variables: {
					objectKey: pendingUpload.objectKey,
					evaluationFormID,
					selectedFields: pendingUpload.selectedFields,
					excludedUserIDs: pendingUpload.excludedUserIDs,
					fieldTypes: toFieldTypeInput(pendingUpload.fieldTypes),
				},
				refetchQueries: ['EvaluationFormRevealUpload'],
			});
			return true;
		} catch {
			addToast(t('conversations.reveal.upload.error'), {
				appearance: 'error',
				autoDismiss: true,
			});
			return false;
		}
	};

	const formInput = (titleItemID?: string) => ({
		title: formik.values.title,
		description: formik.values.description,
		jobTitleIDs: formik.values.jobTitleIDs,
		useOnlyInCycle: formik.values.useOnlyInCycle,
		showAnswersWhenAllUsersShared: formik.values.showAnswersWhenAllUsersShared,
		sections: [
			{
				items: [
					{
						...(titleItemID ? { id: titleItemID } : {}),
						type: graphql.EvaluationSectionItemType.Title,
						fillInBy: graphql.EvaluationFillInBy.Both,
						title: {
							text: formik.values.title,
						},
					},
				],
			},
		],
	});

	const createForm = async (): Promise<{ formID: string; titleItemID: string } | undefined> => {
		const result = await create({
			variables: {
				input: formInput(),
			},
		});

		const form = result.data?.createEvaluationForm.evaluationForm;
		const titleItemID = form?.sections[0]?.items[0]?.id;
		if (!form?.id || !titleItemID) {
			return undefined;
		}

		return { formID: form.id, titleItemID };
	};

	const syncForm = async (formID: string, titleItemID: string) => {
		await update({
			variables: {
				input: {
					id: formID,
					...formInput(titleItemID),
				},
			},
		});
	};

	const onCreate = attempt(async () => {
		let formID = createdFormID;
		let titleItemID = createdTitleItemID;
		if (formID && titleItemID) {
			// Retry path: keep the same template, but persist any field edits
			// made after the failed upload-commit.
			await syncForm(formID, titleItemID);
		} else {
			const created = await createForm();
			if (!created) {
				return;
			}
			formID = created.formID;
			titleItemID = created.titleItemID;
			setCreatedFormID(formID);
			setCreatedTitleItemID(titleItemID);
		}

		// Stay on this page when the upload-commit fails, so the admin can
		// retry (or remove the pending upload) instead of silently ending up
		// with a template without reveal data.
		const committed = await commitPendingUpload(formID);
		if (committed) {
			history.push(`/settings/conversations/${formID}`);
		}
	});

	const handleRemovePendingUpload = () => setPendingUpload(undefined);

	return (
		<>
			<Jumbo
				title={t('settings.conversations.templates.create.title')}
				description={t('settings.conversations.templates.create.description')}
				backButtonLink="/settings/conversations"
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				<Flex mb={2}>
					<Heading3 mb={0}>{t('common.settings.evaluationForms.edit.details')}</Heading3>
				</Flex>
				<GraphQLErrorMessage error={error} showValidationDetails />
				<FormikProvider value={formik}>
					<Form>
						<DetailsTableForm
							formValues={[
								{
									label: t('common.settings.evaluationForms.edit.name'),
									name: 'title',
									type: DetailsTableFormValueTypes.Multilingual,
								},
								{
									label: t('common.settings.evaluationForms.edit.description'),
									name: 'description',
									type: DetailsTableFormValueTypes.MultilingualQuill,
								},
								{
									label: t('common.settings.evaluationForms.edit.showAnswersWhenAllUsersShared'),
									name: 'showAnswersWhenAllUsersShared',
									type: DetailsTableFormValueTypes.Switch,
								},
							]}
						/>

						{revealEnabled && (
							<SettingsSection
								title={t('conversations.reveal.settings.title')}
								actions={<ConversationRevealUploadButton onComplete={setPendingUpload} />}
							>
								<Paragraph mb={0}>{t('conversations.reveal.settings.description')}</Paragraph>
								{pendingUpload && (
									<Callout color="blue">
										<Flex width={1} alignItems="center" justifyContent="space-between">
											<Box>
												<Paragraph mb={2}>
													{t('conversations.reveal.settings.pendingUpload', {
														fileName: pendingUpload.fileName,
														matchedCount: pendingUpload.matchedCount,
													})}
												</Paragraph>
												<Paragraph fontWeight="bold">
													{pendingUpload.selectedFields.join(', ')}
												</Paragraph>
											</Box>
											<Button
												small
												type="secondary"
												title={t('common.delete')}
												onClick={handleRemovePendingUpload}
											/>
										</Flex>
									</Callout>
								)}
							</SettingsSection>
						)}

						<SettingsSection title={t('common.settings.evaluationForms.edit.availability')}>
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.evaluationForms.edit.jobTitleIDs'),
										name: 'jobTitleIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options,
									},
									{
										label: t('common.settings.evaluationForms.edit.useOnlyInCycle'),
										name: 'useOnlyInCycle',
										type: DetailsTableFormValueTypes.Switch,
									},
								]}
							/>
						</SettingsSection>
					</Form>
				</FormikProvider>

				<Flex mt={3}>
					<Button
						title={t('common.next')}
						onClick={onCreate}
						loading={loading || updating || committingUpload}
					/>
				</Flex>
			</Container>
		</>
	);
}
